import { Link } from 'react-router';

import { DetailSection, Field, FieldGrid } from '@/components/admin/detail-parts';
import { VideoPlayer } from '@/components/admin/video-player';
import { StatusBadge } from '@/components/ui/status-badge';
import { DOCUMENT_STATUS, type AdminTalentVideoIntro } from '@/contracts';
import { useTranslations } from '@/i18n/use-locale';
import { isoDate } from '@/lib/detail';
import { formatBytes, formatDuration } from '@/lib/format';
import { useDocumentRejectionLabel, useDocumentStatusLabel } from '@/lib/labels';
import { documentTone } from '@/lib/status';

/**
 * The talent's current video introduction, in whatever state it is in: the pill, the
 * running time and size, the reason on a rejection, and the player. The decision is
 * taken in the document audit, and a video waiting there links to it.
 */
export function VideoIntroSection({
  video,
  talentName,
}: {
  /** Absent on an API that predates the video; null when the talent has none. */
  video: AdminTalentVideoIntro | null | undefined;
  talentName: string;
}) {
  const t = useTranslations('admin.talentDetail');
  const documents = useTranslations('admin.documents');
  const statusLabel = useDocumentStatusLabel();
  const rejectionLabel = useDocumentRejectionLabel();

  return (
    <DetailSection title={t('section.video')}>
      {!video ? (
        <p className="text-[12.5px] text-conc-400">{t('video.empty')}</p>
      ) : (
        <div className="space-y-4">
          <FieldGrid>
            <Field label={t('video.status')}>
              <span className="flex flex-wrap items-center gap-2">
                <StatusBadge tone={documentTone(video.status)}>
                  {statusLabel(video.status)}
                </StatusBadge>
                {video.status === DOCUMENT_STATUS.AWAITING_AUDIT && (
                  <Link
                    to={`/documents/${video.documentId}`}
                    className="text-[12px] font-semibold text-pitch-700 hover:underline"
                  >
                    {t('video.openReview')}
                  </Link>
                )}
              </span>
            </Field>
            <Field label={t('video.uploadedOn')}>
              <span className="num">{isoDate(video.createdAt)}</span>
            </Field>
            <Field label={t('video.duration')}>
              <span className="num">
                {video.durationSeconds !== undefined
                  ? formatDuration(video.durationSeconds)
                  : documents('video.noDuration')}
              </span>
            </Field>
            <Field label={t('video.size')}>
              {video.sizeBytes !== undefined ? (
                <span className="num">{formatBytes(video.sizeBytes)}</span>
              ) : null}
            </Field>
            {video.rejectionReason && (
              <Field label={t('video.reason')} className="sm:col-span-2">
                <span dir="auto">{rejectionLabel(video.rejectionReason)}</span>
              </Field>
            )}
          </FieldGrid>
          {video.previewUrl ? (
            <VideoPlayer
              src={video.previewUrl}
              label={documents('video.player', { name: talentName })}
              className="block w-full max-w-md"
            />
          ) : (
            <p className="text-[12.5px] text-conc-400">{documents('video.noPreview')}</p>
          )}
        </div>
      )}
    </DetailSection>
  );
}
